// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the Layers tab of the Tools panel (docs/AYNI_TOOLS_DATA_PLAN.md):
 * two cards, **Overlay** and **Real time** (the owner's decision,
 * 2026-10-02: one layer each; since 2026-10-09 the Overlay card holds two,
 * `MAX_OVERLAYS`, and the Real time card as many as the device plays). The
 * Basemap card went on 2026-10-12: the globe's own Earth (NASA GIBS) is the
 * only basemap. A card shows its
 * current choice with a thumbnail; pressing it opens the list of choices,
 * each with its thumbnail, "None" first. The cards carry AYNI 2D's section
 * colours (overlays red, real time cyan) inside this site's
 * green→yellow identity. Each overlay's line colour sits under the
 * Overlay card.
 *
 * Renders into the `#tools-menu-layers` section toolsMenuUI leaves for it,
 * and re-renders whenever main.ts hands it a new catalog or selection —
 * including the defaults a newly loaded dataset starts with (the automatic
 * borders take the overlay slot). A card stays open across re-renders until
 * a choice is made. The section stays hidden while there is nothing to
 * choose.
 *
 * AYNI (owner, 2026-10-04): each card's description is a hint on its title
 * (hover, focus or tap), not a paragraph, so the cards keep their room. The
 * Real time list has a search field, is grouped by topic and sorted A–Z
 * inside each, shows each stream's dates against the dataset's, and leaves
 * out streams with no dates in common with the dataset (with a note saying
 * how many). Mock: https://claude.ai/artifact/HcX9amEMNPs2ZUMHGvDH6F
 */

import { t, tAttr, tHtml, type MessageKey } from '../i18n'
import { escapeAttr, escapeHtml } from './domUtils'
import { formatDate } from '../i18n/format'
import { MAX_OVERLAYS, tintFor, toggleOverlay, type CatalogLayer, type LayerSelection, type MapLayerTint } from '../services/mapLayers'
import { DATA_KIND_LABEL, type DataKind, type StreamPackage } from '../services/datasetTaxonomy'
import { titleTier } from '../services/smartSearch'

/** A stream that can play over the dataset as its real-time overlay. */
export interface RtOverlayChoice {
  readonly id: string
  readonly title: string
  /** Its catalog thumbnail, when it has one. */
  readonly thumb?: string
  /** The AYNI XR package it belongs to (a mark only; nothing is blocked by it yet). */
  readonly pkg?: StreamPackage | null
  /**
   * AYNI (owner, 2026-10-20): what kind of data it is (live, forecast,
   * reanalysis, historical, projection), as a pill on its row: the three
   * precipitation streams were easy to confuse without it.
   */
  readonly kind?: DataKind
  /** AYNI: its topic, for grouping (a label), and where that topic sorts. */
  readonly topic?: string
  readonly topicRank?: number
  /**
   * AYNI: the dates it has data for. Undefined while they are being
   * fetched; null when it has none to give (it is then always listed).
   */
  readonly range?: TimeRange | null
}

/** A real-time overlay hidden for now: the dataset's date is outside its dates. */
export interface RtHidden {
  readonly title: string
  readonly startMs: number
  readonly endMs: number
}

/** A span of dates, ms since epoch, end exclusive. */
export interface TimeRange {
  readonly startMs: number
  readonly endMs: number
}

/** AYNI: whether two spans share any date. */
export function rangesOverlap(a: TimeRange, b: TimeRange): boolean {
  return a.startMs < b.endMs && b.startMs < a.endMs
}

export interface LayerPickerHandle {
  /** Re-render for a catalog and the selection currently applied. */
  update(catalog: readonly CatalogLayer[], selection: LayerSelection): void
  /**
   * Re-render the real-time overlay choice: the candidates, the ones
   * playing (first chosen first), and how many may play at once (AYNI:
   * two, one on a phone, tablet or Quest; `maxRtOverlays`).
   */
  /** `planLimited`: the cap is the plan's (Plus 1), not the device's, so the line says so. */
  updateRtOverlay(choices: readonly RtOverlayChoice[], selectedIds: readonly string[], cap?: number, planLimited?: boolean): void
  /**
   * Say which real-time overlays are hidden because the dataset's date is
   * outside their dates (each with its dates), or clear that (empty).
   */
  setRtOverlayStatus(outOfRange: readonly RtHidden[]): void
  /**
   * AYNI: the dataset's dates (null when it has none) and its title: the
   * Real time list leaves out streams with no dates in common with them.
   */
  setRtBase(range: TimeRange | null, title: string): void
}

type CardKind = 'overlay' | 'rt'

interface Option {
  readonly id: string | null
  readonly title: string
  readonly thumb?: string
  readonly pkg?: StreamPackage | null
}

const PACKAGE_MARK: Record<StreamPackage, MessageKey> = {
  'xr-atmosphere': 'tools.layers.pkg.atmosphere',
  'xr-ocean': 'tools.layers.pkg.ocean',
  'xr-climate': 'tools.layers.pkg.climate',
  'xr-fire-aq': 'tools.layers.pkg.fireAq',
  'xr-hazards': 'tools.layers.pkg.hazards',
}

const TINTS: readonly MapLayerTint[] = ['white', 'black', 'source']
const TINT_LABEL: Record<MapLayerTint, MessageKey> = {
  white: 'tools.layers.tint.white',
  black: 'tools.layers.tint.black',
  source: 'tools.layers.tint.source',
}
const CARD_TITLE: Record<CardKind, MessageKey> = {
  overlay: 'tools.layers.overlay',
  rt: 'tools.layers.rt',
}
const CARD_NOTE: Record<CardKind, MessageKey> = {
  overlay: 'tools.layers.overlay.note',
  rt: 'tools.layers.rt.note',
}
const NONE_LABEL: Record<CardKind, MessageKey> = {
  overlay: 'tools.layers.overlay.none',
  rt: 'tools.layers.rt.none',
}

function thumbHtml(kind: CardKind, option: Option): string {
  if (option.id === null) return `<span class="tools-layer-thumb tools-layer-thumb-none" aria-hidden="true"></span>`
  if (!option.thumb) return `<span class="tools-layer-thumb tools-layer-thumb-live" aria-hidden="true">${kind === 'rt' ? tHtml('tools.layers.rt.liveBadge') : ''}</span>`
  return `<img class="tools-layer-thumb" src="${escapeAttr(option.thumb)}" alt="" loading="lazy" decoding="async" width="48" height="24">`
}

export function mountLayerPicker(
  onChange: (selection: LayerSelection) => void,
  /** A stream was picked (or unpicked, if it was playing), or None (null): main.ts keeps the list. */
  onRtOverlayChange: (id: string | null) => void = () => {},
  /** AYNI: the Real time list was opened: a good moment to fetch the streams' dates. */
  onRtListOpen: () => void = () => {},
): LayerPickerHandle {
  let catalog: readonly CatalogLayer[] = []
  let selection: LayerSelection = { overlays: [] }
  let rtChoices: readonly RtOverlayChoice[] = []
  let rtSelected: readonly string[] = []
  let rtCap = 1
  let rtPlanLimited = false
  let rtStatus: readonly RtHidden[] = []
  let rtBase: TimeRange | null = null
  let rtBaseTitle = ''
  /** What is typed in the Real time search, kept across re-renders. */
  let rtQuery = ''
  /** The card whose list is open, kept across re-renders. */
  let open: CardKind | null = null

  function rtStatusText(): string {
    const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' }
    // The end is exclusive: the last date shown is the one just before it.
    return rtStatus.map((h) => t('tools.layers.rt.outOfRangeNamed', {
      title: h.title,
      start: formatDate(h.startMs, opts),
      end: formatDate(h.endMs - 1, opts),
    })).join(' ')
  }

  /** The card's description, as a hint on its title (hover, focus or tap) rather than a paragraph. */
  function titleHtml(kind: CardKind): string {
    const tipId = `tools-layer-tip-${kind}`
    return `
          <h4 class="tools-layer-title">
            <span class="tools-layer-title-text" tabindex="0" aria-describedby="${tipId}">${tHtml(CARD_TITLE[kind])}<span class="tools-layer-info" aria-hidden="true">i</span></span>
            <span class="tools-layer-tip" role="tooltip" id="${tipId}">${tHtml(CARD_NOTE[kind])}</span>
          </h4>`
  }

  function optionHtml(kind: CardKind, o: Option, checked: boolean, extra = '', nameHtml?: string, tip?: string, role = 'radio'): string {
    return `
          <button type="button" role="${role}"${tip ? ` title="${escapeAttr(tip)}"` : ''} class="tools-layer-option${checked ? ' selected' : ''}" aria-checked="${checked}" data-kind="${kind}" data-id="${o.id === null ? '' : escapeAttr(o.id)}"${kind === 'overlay' && o.id !== null ? ` data-layer-id="${escapeAttr(o.id)}"` : ''}>
            ${thumbHtml(kind, o)}
            <span class="tools-layer-name">${nameHtml ?? escapeHtml(o.title)}</span>
            ${o.pkg ? `<span class="tools-layer-pkg" data-package="${o.pkg}">${tHtml(PACKAGE_MARK[o.pkg])}</span>` : ''}
            ${extra}
          </button>`
  }

  function card(kind: CardKind, options: readonly Option[], selectedId: string | null, extra = '', listHtml?: string, many?: { names: string; count: string; ids?: readonly string[]; note?: string }): string {
    const all: Option[] = [{ id: null, title: t(NONE_LABEL[kind]) }, ...options]
    const current = all.find((o) => o.id === selectedId) ?? all[0]
    // AYNI: a card holding several (the overlays) ticks each, as the Real time list does.
    const ids = many?.ids
    const role = ids ? 'checkbox' : 'radio'
    const checked = (o: Option) => (ids ? (o.id === null ? ids.length === 0 : ids.includes(o.id)) : o.id === selectedId)
    const listId = `tools-layer-list-${kind}`
    const isOpen = open === kind
    return `
      <div class="tools-layer-card" data-kind="${kind}">
        <div class="tools-layer-head">
          ${titleHtml(kind)}
          <span class="tools-layer-count">${many?.count ?? (selectedId ? '1/1' : '0/1')}</span>
        </div>
        <button type="button" class="tools-layer-current" id="tools-layer-current-${kind}" aria-expanded="${isOpen}" aria-controls="${listId}">
          ${thumbHtml(kind, current)}
          <span class="tools-layer-name">${escapeHtml(many?.names ?? current.title)}</span>
          <span class="tools-layer-caret" aria-hidden="true">▾</span>
        </button>
        ${listHtml ?? `
        <div class="tools-layer-list" id="${listId}" role="${ids ? 'group' : 'radiogroup'}" aria-label="${tAttr(CARD_TITLE[kind])}"${isOpen ? '' : ' hidden'}>
          ${many?.note ? `<p class="tools-layer-cap">${escapeHtml(many.note)}</p>` : ''}
          ${all.map((o) => optionHtml(kind, o, checked(o), '', undefined, undefined, role)).join('')}
        </div>`}
        ${extra}
      </div>`
  }

  // --- AYNI: the Real time list -------------------------------------------

  /** The streams the list may show: those sharing dates with the dataset, and the one playing. */
  function rtListed(): { listed: RtOverlayChoice[]; hidden: number } {
    const base = rtBase
    if (!base) return { listed: [...rtChoices], hidden: 0 }
    const listed = rtChoices.filter((c) => !c.range || rangesOverlap(c.range, base) || rtSelected.includes(c.id))
    return { listed, hidden: rtChoices.length - listed.length }
  }

  /** A stream's dates as a short range: years for a long one, days for a short one. */
  function rangeText(range: TimeRange): string {
    const years = range.endMs - range.startMs > 3 * 365 * 86_400_000
    const opts: Intl.DateTimeFormatOptions = years ? { year: 'numeric', timeZone: 'UTC' } : { day: 'numeric', month: 'short', timeZone: 'UTC' }
    return `${formatDate(range.startMs, opts)} – ${formatDate(range.endMs - 1, opts)}`
  }

  /** A small bar: the stream's dates in yellow over the dataset's, outlined in green. */
  function miniBar(range: TimeRange): string {
    if (!rtBase) return ''
    const lo = Math.min(rtBase.startMs, range.startMs)
    const hi = Math.max(rtBase.endMs, range.endMs)
    if (!(hi > lo)) return ''
    const pct = (ms: number) => ((ms - lo) / (hi - lo)) * 100
    const base = `inset-inline-start:${pct(rtBase.startMs).toFixed(1)}%;inline-size:${(pct(rtBase.endMs) - pct(rtBase.startMs)).toFixed(1)}%`
    const own = `inset-inline-start:${pct(range.startMs).toFixed(1)}%;inline-size:${Math.max(3, pct(range.endMs) - pct(range.startMs)).toFixed(1)}%`
    return `<span class="tools-rt-mini" aria-hidden="true"><b style="${base}"></b><i style="${own}"></i></span>`
  }

  function highlight(title: string, query: string): string {
    if (!query) return escapeHtml(title)
    const at = title.toLowerCase().indexOf(query)
    if (at < 0) return escapeHtml(title)
    return `${escapeHtml(title.slice(0, at))}<mark>${escapeHtml(title.slice(at, at + query.length))}</mark>${escapeHtml(title.slice(at + query.length))}`
  }

  /** The rows under the search field: None, then each topic's streams A–Z. */
  function rtRowsHtml(): string {
    const { listed, hidden } = rtListed()
    const q = rtQuery.trim().toLowerCase()
    const kindText = (c: RtOverlayChoice) => (c.kind ? t(DATA_KIND_LABEL[c.kind]) : '')
    const match = listed.filter((c) => !q || `${c.title} ${c.topic ?? ''} ${kindText(c)}`.toLowerCase().includes(q))
    const other = t('tools.layers.rt.topicOther')
    const rankOf = (label: string) => match.find((c) => (c.topic || other) === label)?.topicRank ?? Number.MAX_SAFE_INTEGER
    // AYNI (owner, 2026-10-11): while searching, titles starting with the
    // words first, then titles with them, then a topic match — and the
    // topic holding the best title first.
    const tier = (title: string) => (q ? titleTier(title, q) : 2)
    const bestTier = (label: string) => Math.min(...match.filter((c) => (c.topic || other) === label).map((c) => tier(c.title)))
    const topics = [...new Set(match.map((c) => c.topic || other))]
      .sort((a, b) => (q ? bestTier(a) - bestTier(b) : 0) || rankOf(a) - rankOf(b) || a.localeCompare(b))
    const role = rtCap > 1 ? 'checkbox' : 'radio'
    const none = optionHtml('rt', { id: null, title: t(NONE_LABEL.rt) }, rtSelected.length === 0, '', undefined, undefined, role)
    const groups = topics.map((topic) => {
      const rows = match.filter((c) => (c.topic || other) === topic).sort((a, b) => tier(a.title) - tier(b.title) || a.title.localeCompare(b.title))
      return `
          <div class="tools-rt-group" role="presentation"><span>${escapeHtml(topic)}</span><span class="tools-rt-group-count">${rows.length}</span></div>
          ${rows.map((c) => {
            const dates = c.range === undefined
              ? `<span class="tools-rt-dates">${tHtml('tools.layers.rt.datesLoading')}</span>`
              : c.range ? `${miniBar(c.range)}<span class="tools-rt-dates">${escapeHtml(rangeText(c.range))}</span>` : ''
            // Its kind first on the row's second line, then its dates; the package goes in the row's tooltip.
            const kind = c.kind ? `<span class="tools-rt-kind" data-kind="${c.kind}">${escapeHtml(kindText(c))}</span>` : ''
            const tip = c.pkg ? `${c.title} · ${t('tools.layers.rt.package', { name: t(PACKAGE_MARK[c.pkg]) })}` : undefined
            const meta = kind + dates
            return optionHtml('rt', { id: c.id, title: c.title, thumb: c.thumb }, rtSelected.includes(c.id), meta ? `<span class="tools-rt-meta">${meta}</span>` : '', highlight(c.title, q), tip, role)
          }).join('')}`
    }).join('')
    const empty = q && !match.length ? `<p class="tools-rt-empty">${escapeHtml(t('tools.layers.rt.noMatch', { query: rtQuery.trim() }))}</p>` : ''
    const note = hidden ? `<p class="tools-rt-hidden">${escapeHtml(t('tools.layers.rt.hidden', { count: hidden, title: rtBaseTitle }))}</p>` : ''
    return `${none}${empty}${groups}${note}`
  }

  function rtListHtml(): string {
    const isOpen = open === 'rt'
    const count = rtListed().listed.length
    return `
        <div class="tools-layer-list tools-rt-list" id="tools-layer-list-rt"${isOpen ? '' : ' hidden'}>
          <input type="search" class="tools-rt-search" id="tools-layer-rt-search" value="${escapeAttr(rtQuery)}" placeholder="${tAttr('tools.layers.rt.search', { count })}" aria-label="${tAttr('tools.layers.rt.searchAria')}" autocomplete="off" spellcheck="false">
          <p class="tools-rt-cap">${tHtml(rtCap > 1 ? 'tools.layers.rt.capMany' : rtPlanLimited ? 'tools.layers.rt.capPlan' : 'tools.layers.rt.capOne', { count: rtCap })}</p>
          <div class="tools-rt-rows" id="tools-layer-rt-rows" role="${rtCap > 1 ? 'group' : 'radiogroup'}" aria-label="${tAttr(CARD_TITLE.rt)}">${rtRowsHtml()}</div>
        </div>`
  }

  function bindOptions(root: ParentNode): void {
    root.querySelectorAll<HTMLButtonElement>('.tools-layer-option').forEach((btn) => {
      btn.addEventListener('click', () => {
        const kind = btn.dataset.kind as CardKind
        const id = btn.dataset.id || null
        // With room for a second overlay the list stays open, so it can be picked next.
        if (!((kind === 'rt' && rtCap > 1) || kind === 'overlay') || id === null) open = null
        if (kind === 'overlay') {
          // AYNI (2026-10-09): up to two, each pressed on and off; None clears
          // them. Each starts in its own colour (the NOAA overlays: theirs),
          // and keeps the one the viewer gives it.
          const layer = catalog.find((l) => l.id === id)
          onChange({ ...selection, overlays: id && layer ? toggleOverlay(selection.overlays, id, tintFor(layer)) : [] })
        }
        else onRtOverlayChange(id)
        // The host re-renders when the selection comes back; until then, close the list.
        render()
      })
    })
  }

  /** Redraw just the rows, so typing in the search field is never interrupted. */
  function renderRtRows(): void {
    const rows = document.getElementById('tools-layer-rt-rows')
    if (!rows) return
    rows.innerHTML = rtRowsHtml()
    bindOptions(rows)
  }

  function render(): void {
    const host = document.getElementById('tools-menu-layers')
    if (!host) return
    host.hidden = catalog.length === 0 && rtChoices.length === 0
    if (host.hidden) {
      host.innerHTML = ''
      return
    }
    // A re-render (a stream's dates arrived) must not take the search field from under the typist.
    const active = document.activeElement as HTMLInputElement | null
    const typing = active?.id === 'tools-layer-rt-search' ? { start: active.selectionStart, end: active.selectionEnd } : null
    const overlays = catalog.filter((l) => l.kind === 'overlay').map((l) => ({ id: l.id, title: l.title, thumb: l.thumb ?? l.url }))
    const overlayId = selection.overlays[0]?.id ?? null
    const overlayTitle = (id: string) => catalog.find((l) => l.id === id)?.title ?? id
    // AYNI: a colour for each overlay on, named when there are two.
    const tintRow = selection.overlays.map((o, i) => {
      const elId = i === 0 ? 'tools-menu-layers-tint' : `tools-menu-layers-tint-${i + 1}`
      const label = selection.overlays.length > 1 ? t('tools.layers.tintOf', { name: overlayTitle(o.id) }) : t('tools.layers.tint')
      return `
        <div class="tools-menu-language-row tools-layer-tint">
          <label for="${elId}" class="tools-menu-layers-label" title="${escapeAttr(label)}">${escapeHtml(label)}</label>
          <select id="${elId}" class="tools-menu-language-select" data-overlay-index="${i}" aria-label="${escapeAttr(label)}">
            ${TINTS.map((v) => `<option value="${v}"${v === o.tint ? ' selected' : ''}>${tHtml(TINT_LABEL[v])}</option>`).join('')}
          </select>
        </div>`
    }).join('')
    const rtStatusRow = `<p id="tools-menu-layers-rt-status" class="tools-menu-layers-label" role="status"${rtStatus.length ? '' : ' hidden'}>${escapeHtml(rtStatusText())}</p>`
    host.setAttribute('aria-label', t('tools.layers.aria'))
    host.innerHTML = [
      overlays.length ? card('overlay', overlays, overlayId, tintRow, undefined, {
        names: selection.overlays.length ? selection.overlays.map((o) => overlayTitle(o.id)).join(' + ') : t(NONE_LABEL.overlay),
        count: `${selection.overlays.length}/${MAX_OVERLAYS}`,
        ids: selection.overlays.map((o) => o.id),
        note: t('tools.layers.overlay.cap', { count: MAX_OVERLAYS }),
      }) : '',
      rtChoices.length ? card('rt', rtChoices.map((c) => ({ id: c.id, title: c.title, thumb: c.thumb, pkg: c.pkg })), rtSelected[0] ?? null, rtStatusRow, rtListHtml(), {
        names: rtSelected.length ? rtSelected.map((id) => rtChoices.find((c) => c.id === id)?.title ?? id).join(' + ') : t(NONE_LABEL.rt),
        count: `${rtSelected.length}/${rtCap}`,
      }) : '',
    ].join('')

    host.querySelectorAll<HTMLButtonElement>('.tools-layer-current').forEach((btn) => {
      btn.addEventListener('click', () => {
        const kind = btn.id.replace('tools-layer-current-', '') as CardKind
        open = open === kind ? null : kind
        render()
        if (open === 'rt') onRtListOpen()
        ;(document.getElementById(`tools-layer-current-${kind}`) as HTMLButtonElement | null)?.focus()
      })
    })
    bindOptions(host)
    const searchEl = host.querySelector<HTMLInputElement>('#tools-layer-rt-search')
    searchEl?.addEventListener('input', () => {
      rtQuery = searchEl.value
      renderRtRows()
    })
    // Escape in a filled search clears it, rather than closing the Tools panel.
    searchEl?.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape' || !searchEl.value) return
      e.stopPropagation()
      searchEl.value = ''
      rtQuery = ''
      renderRtRows()
    })
    if (typing && searchEl) {
      searchEl.focus()
      searchEl.setSelectionRange(typing.start, typing.end)
    }
    host.querySelectorAll<HTMLSelectElement>('.tools-layer-tint select').forEach((el) => {
      el.addEventListener('change', () => {
        const next = el.value as MapLayerTint
        const index = Number(el.dataset.overlayIndex)
        onChange({ ...selection, overlays: selection.overlays.map((o, i) => (i === index ? { ...o, tint: next } : o)) })
      })
    })
  }

  return {
    update(nextCatalog, nextSelection) {
      catalog = nextCatalog
      selection = nextSelection
      render()
    },
    updateRtOverlay(choices, selectedIds, cap = 1, planLimited = false) {
      rtChoices = choices
      rtSelected = selectedIds
      rtCap = Math.max(1, cap)
      rtPlanLimited = planLimited
      if (!selectedIds.length) rtStatus = []
      render()
    },
    setRtOverlayStatus(outOfRange) {
      rtStatus = outOfRange
      // In place, not a re-render: this changes as the base plays, and a
      // re-render would close a list the viewer has open.
      const el = document.getElementById('tools-menu-layers-rt-status')
      if (!el) return
      el.hidden = !rtStatus.length
      el.textContent = rtStatusText()
    },
    setRtBase(range, title) {
      if (range?.startMs === rtBase?.startMs && range?.endMs === rtBase?.endMs && title === rtBaseTitle) return
      rtBase = range
      rtBaseTitle = title
      render()
    },
  }
}
